import {Injectable} from '@angular/core';
import {Query} from '../../query/data/query';
import {EventEx} from '../../app-common/data/Event';
import {AppService} from '../../app-common/services/app/app.service';
import {PleaseWaitService} from '../../app-common/services/please-wait/please-wait.service';
import {RequestsWebsocketService} from '../../app-common/services/server/requests-websocket.service';
import {AuthService} from '../../auth/services/auth/auth.service';
import {ContainsValuePropertyFilter} from '../../query/data/containsValuePropertyFilter';
import {QueryableProperty} from '../../query/data/queryableProperty';
import {QueryCreator, QueryDTO, QueryFilter, QuerySort} from '../../query/data/queryCreator';
import {CustomerProperties} from '../../query/data/specifics/customerProperties';
import {Customer} from '../data/customer';
import {CustomerDto} from '../data/customerDto';
import {CustomerUtils} from '../data/customerUtils';
import {AddCustomerRequest} from '../data/requests/addCustomerRequest';
import {EditCustomerRequest} from '../data/requests/editCustomerRequest';
import {ApiService} from '../../app-common/services/server/api.service';
import {ConflictException} from '../../app-common/services/server/conflictException';
import {Observable, Subject} from 'rxjs';
import {LoggerService} from '../../logging/services/logger.service';
import {LogLevel} from '../../logging/data/logLevel';
import {LogSourceType} from '../../logging/data/logSourceType';
import {LogSource} from '../../logging/data/logSource';
import {GenericVersion} from '../../audit/data/GenericVersion';

@Injectable({
  providedIn: 'root'
})
export class CustomersService {
  static instance: CustomersService;
  onConflict: EventEx = new EventEx();

  private switchToDisplay = new Subject<void>();
  private refreshListFlag = new Subject<void>();

  constructor(public authService: AuthService,
              public appService: AppService,
              public httpService: ApiService,
              public pleaseWaitService: PleaseWaitService,
              public websocket: RequestsWebsocketService,
              private loggerService: LoggerService) {
    CustomersService.instance = this;
    this.authService = authService;
    this.websocket = websocket;
    this.appService = appService;
  }

  triggerSwitchToDisplay(): void {
    this.switchToDisplay.next();
  }

  listenToSwitchToDisplay(): Observable<void> {
    return this.switchToDisplay.asObservable();
  }

  listenToCustomerListReload(): Observable<void> {
    return this.refreshListFlag.asObservable();
  }

  getEmptyCustomer() {
    const customer = new Customer();
    CustomerUtils.setDefaultProperties(customer);
    return customer;
  }

  getEmptyDto() {
    const dto = new CustomerDto();
    dto.customer = this.getEmptyCustomer();
    dto.categories = [];
    return dto;
  }

  buildSearchQueryModel(query: string, entriesPerPage: number, pageNumber: number, categoryFilter: number, leadFilter: boolean): QueryDTO {
    const customerProperties = new CustomerProperties();
    const rootQuery = new Query();

    for (const property of customerProperties.properties) {
      if (property.name === 'id') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'contactName') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'companyName') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'dba1') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'email') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'phoneNumber') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'address1') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'city') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'state') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'county') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'zip') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
    }
    const sorting = new QuerySort();
    sorting.dir = customerProperties.sortingDescending ? 'desc' : 'asc';
    sorting.field = customerProperties.sortingPropertyName;

    const queryDTO = QueryCreator.generateFilter(rootQuery, sorting, entriesPerPage, pageNumber, 'or');
    if (leadFilter === null || leadFilter === undefined) {
      return queryDTO;
    }

    const queryFilterBackup = queryDTO.union[0].filter;
    const isLeadFilter = new QueryFilter();
    isLeadFilter.field = 'isLead';
    isLeadFilter.operator = 'eq';
    isLeadFilter.value = leadFilter;
    const rootFilter = new QueryFilter();
    rootFilter.filters = [];
    rootFilter.filters.push(queryFilterBackup, isLeadFilter);
    rootFilter.logic = 'and';
    queryDTO.union[0].filter = rootFilter;

    return queryDTO;
  }

  async searchCustomer(query: string, entriesPerPage: number, pageNumber: number, categoryFilter: number, leadFilter: boolean) {
    const finalQueryDto = this.buildSearchQueryModel(query, entriesPerPage, pageNumber, categoryFilter, leadFilter);
    const response = await this.httpService.applicationApiClient.getWithParams<CustomerDto[]>(`Customer/Search`, {filter: JSON.stringify(finalQueryDto)});
    if (response) {
      response.forEach(dto => {
        dto.customer = this.trimCustomerNames(dto.customer);
      });
    }
    return response;
  }

  async getCustomer(customerId: number, force: boolean = false): Promise<CustomerDto> {
    if (customerId <= 0) {
      return undefined;
    }
    const customerDto = await this.httpService.applicationApiClient.getWithParams<CustomerDto>(`Customer/${customerId}`, {force});
    if (customerDto) {
      customerDto.customer = this.trimCustomerNames(customerDto.customer);
      return customerDto;
    }
  }

  async getCustomerHistory(customerId: number, force: boolean = false): Promise<GenericVersion[]> {
    if (customerId <= 0) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CustomerService, 'Failed to fetch contact audit!');
      return undefined;
    }
    return await this.httpService.applicationApiClient.getWithParams<GenericVersion[]>(`Customer/History/${customerId}`, {force});
  }

  async getCustomerVersion(customerId: number, version: number, force: boolean = false): Promise<CustomerDto> {
    if (customerId <= 0 || version <= 0) {
      return undefined;
    }
    return await this.httpService.applicationApiClient.getWithParams<CustomerDto>(`Customer/History/${customerId}/${version}`, null);
  }

  async getCustomerVersionLabels(customerId: number, version: number, force: boolean = false): Promise<string[]> {
    if (customerId <= 0 || version <= 0) {
      return undefined;
    }
    try {
      return await this.httpService.applicationApiClient.getWithParams<string[]>(`Customer/History/${customerId}/${version}/Labels`, null);
    } catch (e) {
      return ['INITIAL'];
    }
  }

  async addCustomer(customer: Customer, categories: number[]) {
    customer = this.trimCustomerNames(customer);
    const request = new AddCustomerRequest();
    this.pleaseWaitService.addToQueue();
    request.customer = customer;
    request.categories = categories;

    let response;
    try {
      response = await this.httpService.applicationApiClient.post(`Customer`, request);
      this.appService.showSuccessMessage('Customer Added', {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.CustomerService, {
        entity: customer,
        message: 'Add contact'
      });
      this.pleaseWaitService.removeFromQueue();

      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CustomerService, {
        entity: ex,
        message: 'Add contact encountered an exception'
      });
      return false;
    }
  }

  async editCustomer(customer: Customer, categories: number[], force: boolean = false) {
    customer = this.trimCustomerNames(customer);
    const request = new EditCustomerRequest();
    this.pleaseWaitService.addToQueue();
    request.customer = customer;
    request.categories = categories;
    request.force = force;


    let response;
    try {
      response = await this.httpService.applicationApiClient.put(`Customer`, customer.id, request);
      this.appService.showSuccessMessage('Customer Edited', {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.CustomerService, {
        entity: customer,
        message: 'Edit contact'
      });
      this.pleaseWaitService.removeFromQueue();
      return response;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      if (ex instanceof ConflictException) {
        const myCust = new CustomerDto();
        myCust.customer = customer;
        myCust.categories = categories;
        this.onConflict.emit({my: myCust, db: ex.conflict});
      }
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CustomerService, {
        entity: ex,
        message: 'Edit contact encountered an exception'
      });
      return false;
    }
  }

  async revertCustomer(customerDto: CustomerDto, version: number): Promise<boolean> {
    let response;
    const customerId = customerDto.customer.id;
    this.pleaseWaitService.addToQueue();
    try {
      response = await this.httpService.applicationApiClient.get(`Customer/History/${customerId}/${version}/Revert`);
      this.appService.showSuccessMessage('Customer Edited', {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.CustomerService, {
        entity: response,
        message: 'Revert contact'
      });
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      if (ex instanceof ConflictException) {
        const myCust = new CustomerDto();
        myCust.customer = response.Customer;
        myCust.categories = response.Categories;
        this.onConflict.emit({my: myCust, db: ex.conflict});
      }
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CustomerService, {
        entity: ex,
        message: 'Revert customer encountered an exception'
      });
      return false;
    }
  }

  async deleteLeadOrCustomer(leadOrCustomerId: number, force: boolean) {
    this.pleaseWaitService.addToQueue();
    let response;
    try {
      response = await this.httpService.applicationApiClient.delete(`Customer`, leadOrCustomerId);
      this.appService.showSuccessMessage(`Customer Deleted`, {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.CustomerService, {
        entity: leadOrCustomerId,
        message: 'Delete customer'
      });
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CustomerService, {
        entity: ex,
        message: 'Delete customer encountered an exception '
      });
      return false;
    }
  }

  trimCustomerNames(customer: Customer) {
    if (customer) {
      if (customer.contactName) {
        customer.contactName = customer.contactName.trim();
      }
      if (customer.companyName) {
        customer.companyName = customer.companyName.trim();
      }
      if (customer.modifiedBy) {
        customer.modifiedBy = customer.modifiedBy.trim();
      }
      if (customer.createdBy) {
        customer.createdBy = customer.createdBy.trim();
      }
      if (customer.email) {
        customer.email = customer.email.trim();
      }
      if (customer.phoneNumber) {
        customer.phoneNumber = customer.phoneNumber.trim();
      }
      if (customer.address1) {
        customer.address1 = customer.address1.trim();
      }
      if (customer.city) {
        customer.city = customer.city.trim();
      }
      if (customer.state) {
        customer.state = customer.state.trim();
      }
      if (customer.zip) {
        customer.zip = customer.zip.trim();
      }
    }
    return customer;
  }

  private createContainsFilter(property: QueryableProperty, toSearch: any) {
    const result = new ContainsValuePropertyFilter();
    result.active = true;
    result.property = property;
    result.queryValue = toSearch;
    return result;
  }
}
